import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import { apiUrl } from '../config/api';
import { AuthContext } from '../context/AuthContext';

// The NAMFISA licence banner on the dashboard: shown from 90 days before the
// licence expires, once it has expired, or while no expiry date is captured.
// When to show it and what it says are the API's
// (GET /api/company-config/licence-status).
const LicenceAlert = () => {
  const [status, setStatus] = useState(null);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const perms = Array.isArray(user?.permissions) ? user.permissions : [];
  const canEdit = perms.includes('*') || perms.includes('CompanyConfig') || perms.includes('ManageConfig');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl('/api/company-config/licence-status'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setStatus(res.data || null);
      } catch {
        // Not available: no banner.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!status?.show) return null;
  const expired = status.level === 'expired';
  const colours = expired
    ? 'bg-rose-50 border-rose-300 text-rose-800 dark:bg-rose-900/30 dark:border-rose-700 dark:text-rose-200'
    : 'bg-amber-50 border-amber-300 text-amber-900 dark:bg-amber-900/25 dark:border-amber-700 dark:text-amber-200';
  const Icon = expired ? ShieldAlert : AlertTriangle;
  const title = expired
    ? 'NAMFISA licence expired'
    : status.level === 'missing'
      ? 'NAMFISA licence expiry date not captured'
      : `NAMFISA licence expires in ${status.days_left} day${status.days_left === 1 ? '' : 's'}`;

  return (
    <div role="alert" className={`mb-4 flex items-start gap-3 rounded-xl border-2 px-4 py-3 ${colours}`}>
      <Icon className="mt-0.5 h-6 w-6 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-bold">{title}</div>
        <div className="text-sm mt-0.5">{status.message}</div>
      </div>
      {canEdit && (
        <button
          type="button"
          onClick={() => navigate('/company-config')}
          className={`flex-shrink-0 self-center rounded-lg px-3 py-1.5 text-sm font-semibold text-white ${expired ? 'bg-rose-600 hover:bg-rose-700' : 'bg-amber-600 hover:bg-amber-700'}`}
        >
          Update in Company Config
        </button>
      )}
    </div>
  );
};

export default LicenceAlert;
